import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FileSpreadsheet,
  BookOpen,
  ClipboardCheck,
  Download,
  PenTool,
  Layers,
  Gauge,
  Search,
  ArrowUpRight,
} from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import PageHero, { SectionHeader } from "../components/shared/PageHero";
import LeadCaptureModal from "../components/shared/LeadCaptureModal";

const RESOURCES = [
  {
    icon: Search,
    type: "Tool",
    title: "Free Interactive ASO Audit Tool",
    desc: "Drop your email + App Store URL. Get an instant 12-point audit of your listing, keywords and conversion potential.",
    cta: "Run free audit",
    gated: "email + app URL",
    action: { kind: "link", to: "/free-aso-audit" },
  },
  {
    icon: ClipboardCheck,
    type: "Checklist",
    title: "App Launch Checklist 2026",
    desc: "47-point pre-launch checklist — metadata, screenshots, keywords, ratings, analytics. PDF delivered to email.",
    cta: "Download PDF",
    gated: "email gate",
    action: {
      kind: "modal",
      modal: {
        eyebrow: "App Launch Checklist 2026",
        title: "Where should we send the 47-point PDF?",
        description: "Delivered to your email instantly. WhatsApp follow-up with a quick walkthrough.",
        ctaLabel: "Send the checklist",
        successTitle: "Checklist on its way!",
        successBody: "Open your inbox in 2 minutes. The 47-point PDF is yours.",
        source: "resource-launch-checklist",
      },
    },
  },
  {
    icon: FileSpreadsheet,
    type: "Template",
    title: "ASO Keyword Research Template",
    desc: "The Google Sheet our senior strategists use for keyword discovery, clustering and difficulty scoring.",
    cta: "Get the Sheet",
    gated: "free",
    action: {
      kind: "modal",
      modal: {
        eyebrow: "ASO Keyword Research Template",
        title: "Where should we send the Google Sheet link?",
        description: "We'll email you a link to a 'view-only' copy you can duplicate.",
        ctaLabel: "Send the Sheet",
        successTitle: "Sheet link on the way!",
        successBody: "Check your inbox. Make a copy and you're set.",
        source: "resource-keyword-template",
      },
    },
  },
  {
    icon: Gauge,
    type: "Tool",
    title: "A/B Test ROI Calculator",
    desc: "Interactive calculator that turns conversion-rate lift into incremental installs, revenue and paid-media savings.",
    cta: "Open calculator",
    gated: "interactive",
    action: {
      kind: "modal",
      modal: {
        eyebrow: "A/B Test ROI Calculator",
        title: "Get access to the live calculator",
        description: "We'll email you the calculator link plus a 5-minute video explainer.",
        ctaLabel: "Send me access",
        successTitle: "Access incoming!",
        successBody: "Calculator + video explainer in your inbox in 2 minutes.",
        source: "resource-roi-calculator",
      },
    },
  },
  {
    icon: Layers,
    type: "Case Pack",
    title: "Lumotis Case Study Pack",
    desc: "3 full-length case studies: FinTech #38 → #2, Casual Gaming USA Top 10, Health 4.2★ → 4.8★. Full methodology.",
    cta: "Get the pack",
    gated: "3-case PDF",
    action: {
      kind: "modal",
      modal: {
        eyebrow: "Lumotis Case Study Pack",
        title: "Where should we send the 3-case PDF?",
        description: "Three deep-dive case studies with audit, approach, results and what we'd do differently.",
        ctaLabel: "Send the pack",
        successTitle: "Pack en route!",
        successBody: "Email + WhatsApp delivery within 2 minutes.",
        source: "resource-case-pack",
      },
    },
  },
  {
    icon: PenTool,
    type: "Guide",
    title: "App Rating Recovery Guide",
    desc: "The exact playbook we use to lift apps from 3.x to 4.5+ stars in 60 days — prompts, sentiment filters, reply ops.",
    cta: "Download guide",
    gated: "email gate",
    action: {
      kind: "modal",
      modal: {
        eyebrow: "App Rating Recovery Guide",
        title: "Send me the 60-day rating playbook",
        description: "PDF + a Loom walkthrough from our senior reviews lead.",
        ctaLabel: "Send the guide",
        successTitle: "Guide on its way!",
        successBody: "PDF + walkthrough Loom delivered to your email in 2 minutes.",
        source: "resource-rating-guide",
      },
    },
  },
  {
    icon: BookOpen,
    type: "Webinar",
    title: "Double Your App Downloads in 90 Days",
    desc: "Quarterly live webinar with Lumotis senior strategists. Live Q&A, case teardowns and the 90-day ASO sprint framework.",
    cta: "Reserve a seat",
    gated: "quarterly",
    action: {
      kind: "modal",
      modal: {
        eyebrow: "Reserve your seat",
        title: "Double Your App Downloads in 90 Days",
        description: "Live, 60 minutes, with senior strategists. We'll send the calendar invite + Zoom link to your email + WhatsApp.",
        ctaLabel: "Reserve my seat",
        successTitle: "Seat reserved!",
        successBody: "Calendar invite in your inbox. WhatsApp reminder 1 hour before the session.",
        source: "resource-webinar-reservation",
      },
    },
  },
  {
    icon: Download,
    type: "Report",
    title: "ASO Benchmark Report 2026",
    desc: "Annual data report — category benchmarks, keyword difficulty trends, rating medians and LLM-citation rates for apps.",
    cta: "Download report",
    gated: "annual · high GEO value",
    action: {
      kind: "modal",
      modal: {
        eyebrow: "ASO Benchmark Report 2026",
        title: "Get the 32-page benchmark PDF",
        description: "The most comprehensive ASO benchmark dataset of 2026. Email + WhatsApp delivery.",
        ctaLabel: "Send the report",
        successTitle: "Report on its way!",
        successBody: "32-page PDF in your inbox. We'll WhatsApp the chart pack separately.",
        source: "resource-benchmark-2026",
      },
    },
  },
];

export default function ResourcesPage() {
  const [modal, setModal] = useState(null);

  return (
    <>
      <Helmet>
        <title>
          App Growth Resources — ASO Templates, Guides & Playbooks | Lumotis
        </title>
        <meta
          name="description"
          content="Free app growth resources from Lumotis: ASO templates, checklists, push notification copy packs, Apple Search Ads structures, and growth ebooks."
        />
        <link rel="canonical" href="https://lumotis.com/resources" />
      </Helmet>
      <Navbar />
      <main data-testid="resources-page">
        <PageHero
          testid="resources-hero"
          eyebrow="Resources"
          breadcrumb={[{ label: "Home", href: "/" }, { label: "Resources" }]}
          title="Templates, checklists and playbooks, all"
          titleAccent="free."
          subtitle="Everything our team uses internally to ship #1 rankings — open-sourced for founders who want to try it themselves first."
          primaryCta={{ to: "/free-aso-audit", label: "Run a free audit", testid: "resources-cta-primary" }}
          secondaryCta={{ to: "/contact", label: "Talk to us", testid: "resources-cta-secondary" }}
        />

        <section className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-200">
          <div className="container">
            <SectionHeader
              eyebrow="01 — Library"
              title="Eight assets, built for app founders."
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
              {RESOURCES.map((r, idx) => {
                const Icon = r.icon;
                const inner = (
                  <>
                    <div className="flex items-center justify-between mb-6">
                      <span className="w-10 h-10 grid place-items-center bg-electric text-white">
                        <Icon className="w-4 h-4" strokeWidth={2.2} />
                      </span>
                      <span className="text-overline text-zinc-500 group-hover:text-zinc-300">
                        {r.type}
                      </span>
                    </div>
                    <h3 className="font-display text-lg font-bold leading-snug">
                      {r.title}
                    </h3>
                    <p className="mt-2 text-sm text-zinc-600 group-hover:text-zinc-300 leading-relaxed flex-1">
                      {r.desc}
                    </p>
                    {r.gated && (
                      <span className="mt-4 inline-flex self-start items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 group-hover:text-electric border-t border-zinc-200 group-hover:border-white/10 pt-3 w-full">
                        <span className="w-1.5 h-1.5 bg-electric" />
                        {r.gated}
                      </span>
                    )}
                    <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-charcoal group-hover:text-electric">
                      {r.cta} <ArrowUpRight className="w-3.5 h-3.5" />
                    </span>
                  </>
                );
                const wrapperCls =
                  "group bg-white p-6 md:p-7 flex flex-col text-left hover:bg-charcoal hover:text-white transition-colors w-full";
                return (
                  <motion.div
                    key={r.title}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: idx * 0.04 }}
                    data-testid={`resource-${idx}`}
                  >
                    {r.action.kind === "link" ? (
                      <Link to={r.action.to} className={wrapperCls}>
                        {inner}
                      </Link>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setModal(r.action.modal)}
                        className={wrapperCls}
                        data-testid={`resource-trigger-${idx}`}
                      >
                        {inner}
                      </button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <CTABanner />
      </main>
      <Footer />

      <LeadCaptureModal
        open={!!modal}
        onClose={() => setModal(null)}
        eyebrow={modal?.eyebrow}
        title={modal?.title}
        description={modal?.description}
        ctaLabel={modal?.ctaLabel}
        successTitle={modal?.successTitle}
        successBody={modal?.successBody}
        source={modal?.source || "resource-download"}
      />
    </>
  );
}
